import Select from 'react-select';
import { Input, Button } from 'reactstrap';
import { AUDIT_ACTIONS, AUDIT_ENTITY_TYPES } from '../../constants/auditActions';
import { velzonSelectStyles, type VpSelectOption } from '../ui/velzonSelectStyles';
import { VpSelect } from '../ui/VpSelect';

export type DatePreset = 'last_24h' | 'last_7d' | 'last_30d' | 'custom';

export interface FilterState {
  datePreset: DatePreset;
  from: string | null;
  to: string | null;
  tenantIds: number[];
  actorUserIds: number[];
  actions: string[];
  entityTypes: string[];
  entityId: string;
  payloadSearch: string;
}

interface SelectOption { id: number; label: string; }

interface Props {
  value: FilterState;
  onChange: (next: FilterState) => void;
  availableTenants: SelectOption[];
  availableActors: SelectOption[];
  hideTenantFilter?: boolean;
  hideAdvanced?: boolean;
}

const toNumOpts = (xs: SelectOption[]): VpSelectOption[] => xs.map((x) => ({ value: x.id, label: x.label }));
const toStrOpts = (xs: readonly string[]): VpSelectOption[] => xs.map((x) => ({ value: x, label: x }));

/**
 * Audit filters. Logic/props unchanged. SP-5h: the multi-value fields
 * (Tenant/Actor/Action/Entity) use react-select (searchable, chips) with the
 * shared Velzon styling; Date preset + text inputs stay native/reactstrap.
 */
export function AuditFilters({ value, onChange, availableTenants, availableActors, hideTenantFilter, hideAdvanced }: Props) {
  const set = (patch: Partial<FilterState>) => onChange({ ...value, ...patch });

  const tenantOpts = toNumOpts(availableTenants);
  const actorOpts = toNumOpts(availableActors);
  const actionOpts = toStrOpts(AUDIT_ACTIONS);
  const entityOpts = toStrOpts(AUDIT_ENTITY_TYPES);

  return (
    <div className="d-flex flex-wrap gap-3 py-2 align-items-end">
      <div className="d-flex flex-column" style={{ minWidth: 170 }}>
        <span className="form-label mb-1 small">Date</span>
        <VpSelect
          ariaLabel="Date"
          options={[
            { value: 'last_24h', label: 'ultime 24h' },
            { value: 'last_7d', label: 'ultimi 7 giorni' },
            { value: 'last_30d', label: 'ultimi 30 giorni' },
            { value: 'custom', label: 'custom' },
          ]}
          value={value.datePreset}
          onChange={(v) => set({ datePreset: v as DatePreset })}
        />
      </div>

      {!hideTenantFilter && (
        <div className="d-flex flex-column" style={{ minWidth: 200 }}>
          <span className="form-label mb-1 small">Tenant</span>
          <Select<VpSelectOption, true>
            isMulti options={tenantOpts}
            value={tenantOpts.filter((o) => value.tenantIds.includes(o.value as number))}
            onChange={(opts) => set({ tenantIds: opts.map((o) => Number(o.value)) })}
            styles={velzonSelectStyles} classNamePrefix="vp-select" placeholder="Tutti…"
          />
        </div>
      )}

      <div className="d-flex flex-column" style={{ minWidth: 200 }}>
        <span className="form-label mb-1 small">Actor</span>
        <Select<VpSelectOption, true>
          isMulti options={actorOpts}
          value={actorOpts.filter((o) => value.actorUserIds.includes(o.value as number))}
          onChange={(opts) => set({ actorUserIds: opts.map((o) => Number(o.value)) })}
          styles={velzonSelectStyles} classNamePrefix="vp-select" placeholder="Tutti…"
        />
      </div>

      {!hideAdvanced && (
        <>
          <div className="d-flex flex-column" style={{ minWidth: 220 }}>
            <span className="form-label mb-1 small">Action</span>
            <Select<VpSelectOption, true>
              isMulti options={actionOpts}
              value={actionOpts.filter((o) => value.actions.includes(o.value as string))}
              onChange={(opts) => set({ actions: opts.map((o) => String(o.value)) })}
              styles={velzonSelectStyles} classNamePrefix="vp-select" placeholder="Tutte…"
            />
          </div>

          <div className="d-flex flex-column" style={{ minWidth: 200 }}>
            <span className="form-label mb-1 small">Entity</span>
            <Select<VpSelectOption, true>
              isMulti options={entityOpts}
              value={entityOpts.filter((o) => value.entityTypes.includes(o.value as string))}
              onChange={(opts) => set({ entityTypes: opts.map((o) => String(o.value)) })}
              styles={velzonSelectStyles} classNamePrefix="vp-select" placeholder="Tutte…"
            />
          </div>

          <label className="d-flex flex-column">
            <span className="form-label mb-1 small">Entity ID</span>
            <Input
              type="text"
              value={value.entityId}
              onChange={(e) => set({ entityId: e.target.value })}
              style={{ minWidth: 100 }}
            />
          </label>

          <label className="d-flex flex-column">
            <span className="form-label mb-1 small">Cerca payload</span>
            <Input
              type="text"
              value={value.payloadSearch}
              onChange={(e) => set({ payloadSearch: e.target.value })}
              placeholder="ILIKE %term%"
              style={{ minWidth: 180 }}
            />
          </label>
        </>
      )}

      <Button
        type="button"
        color="light" size="sm"
        onClick={() => onChange({ datePreset: 'last_24h', from: null, to: null,
          tenantIds: [], actorUserIds: [], actions: [], entityTypes: [],
          entityId: '', payloadSearch: '' })}
      >Reset filtri</Button>
    </div>
  );
}
